import { CreditnoteApi, CreditnoteDataType } from "@/api/CreditnoteApi";
import { DebitnoteApi, DebitnoteDataType } from "@/api/DebitnoteApi";
import { CreditnoteProductDataType } from "@/api/CreditnoteProductApi";
import { DebitnoteProductDataType } from "@/api/DebitnoteProductApi";
import { EasyposEInvoice } from "@/atv/submitInvoice";
import {
    generateCreditFromDebitnote,
    generateDebitNoteFromCreditnote,
    sendECreditnoteData,
    sendEDebitnoteData,
} from "@utils/EDocument";
import {
    buildAutoReferenceFromCreditnote,
    buildAutoReferenceFromDebitnote,
    mergeReferences,
    serializeStoredReferences,
    toInfoRefElements,
} from "@utils/DocumentReferences";

export type EDocumentNoteKind = "Creditnote" | "Debitnote";

export type NoteCancellationResult = {
    /** Tipo de la nota fuente que se anuló. */
    sourceKind: EDocumentNoteKind;
    /** Tipo de la nota contraria creada. */
    counterKind: EDocumentNoteKind;
    counterId?: string;
    counterNumero?: number;
};

export type CancelAcceptedNoteParams = {
    /** Nota fuente con sus líneas (creditnoteProducts / debitnoteProducts) cargadas. */
    note: CreditnoteDataType | DebitnoteDataType;
    kind: EDocumentNoteKind;
    /** Razón de la anulación; se usa como descripción de la nota contraria. */
    reason: string;
    /**
     * true en anulación individual (la nota contraria ajusta inventario); false en la cascada
     * por anulación de factura (la anulación de la factura ya revirtió el inventario).
     */
    touchStock: boolean;
    /** Instancia para enviar a Hacienda; se usa solo si la e-facturación está activa. */
    easyposEInvoice?: EasyposEInvoice;
    /** true si la suscripción tiene facturación electrónica activa. */
    einvoiceActive?: boolean;
};

/**
 * Anula una nota electrónica ACEPTADA creando la nota contraria: una nota de crédito se anula
 * creando una nota de débito y una nota de débito creando una nota de crédito. Agrega la
 * referencia "anular" a la nota contraria, la persiste en `info_references` y la envía a
 * Hacienda cuando la facturación electrónica está activa.
 *
 * Fuente única de la lógica "anular una nota creando la contraria": la usan la cascada de
 * anulación de factura (offcanvas) y los botones "Anular" de las listas de notas.
 */
export const cancelAcceptedNoteWithCounter = async (
    params: CancelAcceptedNoteParams,
): Promise<NoteCancellationResult> => {
    const { note, kind, reason, touchStock, easyposEInvoice, einvoiceActive } = params;

    if (kind === "Creditnote") {
        const creditnote = note as CreditnoteDataType;
        const { debitnote, products } = generateDebitNoteFromCreditnote(creditnote, reason);
        const created = await DebitnoteApi.createDebitnoteAndProducts(
            debitnote,
            products as DebitnoteProductDataType[],
            touchStock,
        );

        if (easyposEInvoice && einvoiceActive) {
            const references = mergeReferences(
                [buildAutoReferenceFromCreditnote(creditnote, "anular", `${debitnote.description}`)],
                [],
            );
            if (created.id) {
                await DebitnoteApi.update(created.id, {
                    info_references: serializeStoredReferences(references),
                });
            }
            await sendEDebitnoteData(easyposEInvoice, `${created.id}`, toInfoRefElements(references));
        }

        return {
            sourceKind: "Creditnote",
            counterKind: "Debitnote",
            counterId: created.id,
            counterNumero: created.numero,
        };
    }

    const debitnoteSource = note as DebitnoteDataType;
    const { creditnote, products } = generateCreditFromDebitnote(debitnoteSource, reason);
    const created = await CreditnoteApi.createCreditnoteAndProducts(
        creditnote,
        products as CreditnoteProductDataType[],
        touchStock,
    );

    if (easyposEInvoice && einvoiceActive) {
        const references = mergeReferences(
            [buildAutoReferenceFromDebitnote(debitnoteSource, "anular", `${creditnote.description}`)],
            [],
        );
        if (created.id) {
            await CreditnoteApi.update(created.id, {
                info_references: serializeStoredReferences(references),
            });
        }
        await sendECreditnoteData(easyposEInvoice, `${created.id}`, toInfoRefElements(references));
    }

    return {
        sourceKind: "Debitnote",
        counterKind: "Creditnote",
        counterId: created.id,
        counterNumero: created.numero,
    };
};
